import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Clock, DollarSign, MessageSquare, FileText,
  ExternalLink, ArrowRight
} from 'lucide-react';
import { Button } from '../ui/button';
import { companyInfo } from '../../data/mock';

const JobTreadSection = () => {
  const benefits = [
    {
      icon: Clock,
      title: "Real-Time Updates",
      description: "Track project progress as it happens, without needing to be on-site."
    },
    {
      icon: DollarSign,
      title: "Financial Transparency",
      description: "Review proposals, view invoices, and make payments securely online."
    },
    {
      icon: MessageSquare,
      title: "Seamless Communication",
      description: "All project communication in one place—no more scattered emails."
    },
    {
      icon: FileText,
      title: "Centralized Documents",
      description: "Access contracts, photos, and project files anytime, anywhere."
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-slate-900 to-slate-800 relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-orange-500 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            {/* We're Better with JobTread Logo */}
            <img 
              src="https://www.jobtread.com/images/resources/marketing/were-better-with-jobtread.svg" 
              alt="We're Better with JobTread" 
              className="h-16 sm:h-20 mb-8"
            />
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Best-in-Class Customer Experience
            </h2>
            
            <p className="text-lg text-slate-300 mb-8 leading-relaxed">
              We use JobTread construction management software to deliver complete transparency, seamless communication, and an exceptional project experience from start to finish. Your dedicated customer portal gives you 24/7 access to everything about your project.
            </p>

            {/* Benefits Grid */}
            <div className="grid sm:grid-cols-2 gap-6 mb-10">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div key={index} className="flex gap-4">
                    <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-cyan-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">{benefit.title}</h3>
                      <p className="text-sm text-slate-400">{benefit.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4">
              <Link to="/jobtread">
                <Button
                  size="lg"
                  className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-8 group"
                >
                  Learn More About JobTread
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <a
                href={companyInfo.jobtreadLoginUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  variant="outline"
                  size="lg"
                  className="border-white/30 text-white hover:bg-white/10 font-semibold px-8 group"
                >
                  Customer Portal Login
                  <ExternalLink className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </div>
          </div>

          {/* Right Content - Device Mockup */}
          <div className="relative">
            <div className="relative">
              <img 
                src="https://www.jobtread.com/images/resources/marketing/customer-kit/jobtread-software-responsive-devices.png"
                alt="JobTread Customer Portal on multiple devices"
                className="w-full rounded-xl"
              />
              {/* Decorative glow */}
              <div className="absolute -inset-4 bg-gradient-to-r from-cyan-500/20 to-orange-500/20 rounded-2xl blur-2xl -z-10" />
            </div>

            {/* Floating badge */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">Free Customer Portal</p>
                  <p className="text-xs text-slate-500">No cost to you</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default JobTreadSection;
